/**
 * Dedicated News & Next Best Actions page - keeps main dashboard uncluttered.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import HeaderNav from './HeaderNav';
import ErrorBoundary from './common/ErrorBoundary';
import AgentNextBestActionsCard from './AgentNextBestActionsCard';
import RealtimeNews from './RealtimeNews';
import AuraFocusStrip from './advice/AuraFocusStrip';
import { ArrowLeftIcon, SparklesIcon, NewspaperIcon } from '@heroicons/react/24/outline';

const DashboardInsightsPage = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('news');
  const [portfolioData, setPortfolioData] = useState(null);
  const [visions, setVisions] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [portfolioRes, visionsRes] = await Promise.all([
          axios.get('/api/portfolio/accounts').catch(() => ({ data: null })),
          axios.get('/api/visions').catch(() => ({ data: { visions: [] } })),
        ]);
        setPortfolioData(portfolioRes.data);
        setVisions(visionsRes.data.visions || []);
      } catch (e) {
        console.error('Insights page load error:', e);
      }
    };
    if (user?.portfolio_connected || user?.onboarding_complete) load();
  }, [user?.portfolio_connected, user?.onboarding_complete]);

  return (
    <div className="min-h-screen bg-bone relative overflow-hidden">
      <HeaderNav
        activeSection="insights"
        onSectionChange={() => {}}
        user={user}
        onLogout={onLogout}
        visionCount={visions.length}
      />

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-8 pb-14">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-secondary hover:text-primary transition-colors duration-300"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Back to Dashboard
          </Link>
        </div>

        <motion.header
          className="mb-8"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-h1 font-display text-primary flex items-center gap-3">
            <SparklesIcon className="w-8 h-8 text-pine" />
            News & Next best actions
          </h1>
          <p className="text-secondary mt-2">
            Market news and personalized action recommendations
          </p>
        </motion.header>

        <AuraFocusStrip className="mb-6" />

        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('news')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-colors duration-300 ${
              activeTab === 'news'
                ? 'nav-tab-active bg-pine-tint border border-hairline'
                : 'nav-tab border border-transparent hover:bg-pine-tint/50'
            }`}
          >
            <NewspaperIcon className="w-5 h-5" />
            News
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('actions')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-colors duration-300 ${
              activeTab === 'actions'
                ? 'nav-tab-active bg-pine-tint border border-hairline'
                : 'nav-tab border border-transparent hover:bg-pine-tint/50'
            }`}
          >
            <SparklesIcon className="w-5 h-5" />
            Next best actions
          </button>
        </div>

        {activeTab === 'news' && (
          <motion.div
            key="news"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="surface-panel p-6"
          >
            <RealtimeNews
              user={user}
              portfolioData={portfolioData}
              visions={visions}
            />
          </motion.div>
        )}

        {activeTab === 'actions' && (
          <motion.div
            key="actions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="surface-panel p-6"
          >
            <ErrorBoundary>
              <AgentNextBestActionsCard />
            </ErrorBoundary>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default DashboardInsightsPage;
